<template>
    <section
        v-if="shouldRender"
        :data-mui-theme="theme"
        class="slice slice-content-columns slice-content-columns--default"
        aria-label="content-columns"
        data-testid="slice-content-columns-default"
    >
        <div class="slice-content">
            <div class="slice-body slice-content-columns--default__grid">
                <div
                    v-for="(column, index) in columns"
                    :key="index"
                    class="slice-content-columns--default__column"
                    :data-testid="`slice-content-columns-column--${index}`"
                >
                    <div
                        v-if="isFilled.image(column.image)"
                        class="slice-content-columns--default__image mui-background"
                        :data-testid="`slice-content-columns-image--${index}`"
                    >
                        <PrismicImage
                            :field="column.image"
                            :alt="column.image.alt"
                            loading="lazy"
                            sizes="(max-width: 767px) 85vw, (min-width: 768px) 33vw"
                        />
                    </div>
                    <MuiTag v-if="isFilled.keyText(column.tag)">{{ column.tag }}</MuiTag>
                    <div v-if="isFilled.keyText(column.title)">
                        <h3 class="slice-content-columns--default__title" :data-testid="`slice-content-columns-title--${index}`">
                            {{ column.title }}
                        </h3>
                        <PrismicRichText
                            v-if="isFilled.richText(column.description)"
                            :field="column.description"
                            :class="[
                                'slice-content-columns--default__description',
                                {'slice-content-columns--default__description--with-checks': hasBulletPoints(column.description)},
                            ]"
                            :data-testid="`slice-content-columns-description--${index}`"
                        />
                    </div>
                    <MuiLink
                        v-if="isFilled.link(column.link) && column.link.url && column.link.text"
                        :href="column.link.url"
                        :text="column.link.text"
                        :target="(column.link as FilledLinkToWebField).target || undefined"
                        class="slice-content-columns--default__link"
                        :data-testid="`slice-content-columns-link--${index}`"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledLinkToWebField, RichTextField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {ContentColumnsSlice} from '@prismicio-types';
    import {MuiTag, MuiLink} from '#components';

    type ContentColumnsDefaultInZone = Extract<ContentColumnsSlice, {variation: 'default'}>;

    const MIN_COLUMNS = 2;
    const MAX_COLUMNS = 3;

    const props = defineProps(getSliceComponentProps<ContentColumnsDefaultInZone>());

    const columns = computed(() => (props.slice.primary.columns ?? []).slice(0, MAX_COLUMNS));

    const columnCount = computed(() => columns.value.length);

    const shouldRender = computed(() => columns.value.length >= MIN_COLUMNS);

    const theme = computed(() => props.slice.primary.theme);

    function hasBulletPoints(field: RichTextField): boolean {
        if (!isFilled.richText(field)) {
            return false;
        }

        return field.some((block) => block.type === 'list-item');
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-content-columns {
        &--default {
            &__grid {
                display: flex;
                gap: var(--joy-core-spacing-8);
                position: relative;
                padding-top: var(--joy-core-spacing-12);
                overflow-x: auto;
                scroll-snap-type: x mandatory;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: none;
                border-top: 1px solid var(--joy-color-neutral-30);

                &::-webkit-scrollbar {
                    display: none;
                }

                @include mq.screen_sm(min) {
                    display: grid;
                    width: 100%;
                    gap: 32px;
                    grid-template-columns: repeat(v-bind(columnCount), minmax(0, 1fr));
                }

                @include mq.screen_md(min) {
                    gap: 64px;
                }
            }

            &__column {
                padding-bottom: var(--joy-core-spacing-2);
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: var(--joy-core-spacing-8);
                flex: 0 0 calc(100% - var(--joy-core-spacing-12));
                scroll-snap-align: start;
                min-width: 0;
                text-align: left;
                position: relative;

                &:not(:first-child)::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: -16px;
                    border-left: 1px solid var(--joy-color-neutral-30);

                    @include mq.screen_sm(min) {
                        left: -16px;
                    }
                }

                @include mq.screen_md(min) {
                    &:not(:first-child)::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        bottom: 0;
                        left: -32px;
                        border-left: 1px solid var(--joy-color-neutral-30);
                    }
                }
            }

            &__link {
                margin-top: auto;
            }

            &__image {
                align-self: stretch;
                width: 100%;
                border-radius: var(--joy-core-radius-4);
                overflow: hidden;
                aspect-ratio: 16 / 9;
                padding: var(--joy-core-spacing-4);

                img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    border-radius: var(--joy-core-radius-4);
                }
            }

            &__title {
                margin: 0;
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-small);
                color: var(--joy-color-text-heading);
                margin-bottom: var(--joy-core-spacing-4);
            }

            &__description {
                align-self: stretch;
                margin: 0;
                font-family: var(--joy-font-family-base);
                font-size: var(--joy-font-size-primary-400);
                line-height: var(--joy-line-height-large);
                color: var(--joy-color-neutral-40);

                p {
                    margin: 0;
                }

                &--with-checks {
                    ul {
                        padding: 0;
                        margin: 0;
                        list-style: none;

                        li {
                            list-style-type: none;
                            padding-left: var(--joy-core-spacing-7);
                            margin-bottom: var(--joy-core-spacing-3);
                            background-image: url(../check.svg);
                            background-repeat: no-repeat;
                            background-position: left center;
                            background-size: 15px 15px;

                            &:last-child {
                                margin-bottom: 0;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
